import { useState } from "react";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useWalletByCustomer, useWalletStatement } from "../hooks/useWallets";
import type { WalletLedgerEntry } from "../services/walletsApi";
import { TopupDialog } from "./TopupDialog";
import { CashOutDialog } from "./CashOutDialog";
import { PointsBalanceCard } from "@/features/loyalty/components/PointsBalanceCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Props {
  customerId: string;
  focusEntryId?: string;
}

const formatKes = (n: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    minimumFractionDigits: 2,
  }).format(n);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-KE", {
    dateStyle: "medium",
    timeStyle: "short",
  });

const directionStyles = (entry: WalletLedgerEntry) =>
  entry.direction === "Credit"
    ? "text-emerald-700 font-semibold"
    : "text-amber-800 font-semibold";

export const WalletTab = ({ customerId, focusEntryId }: Props) => {
  const { hasPermission } = useAuthStore();
  const [topupOpen, setTopupOpen] = useState(false);
  const [cashOutOpen, setCashOutOpen] = useState(false);

  const walletQuery = useWalletByCustomer(customerId);
  const statementQuery = useWalletStatement(customerId, { pageNumber: 1, pageSize: 50 });

  // Surface the anonymous-pool gate cleanly — server returns 409 with errorCode.
  const errorCode = (walletQuery.error as { response?: { data?: { code?: string } } } | undefined)
    ?.response?.data?.code;
  if (errorCode === "Wallet.AnonymousPool" || errorCode === "Wallet_AnonymousPool") {
    return (
      <div className="rounded-md border bg-muted/40 p-4 text-sm text-muted-foreground">
        Wallet is not available for the shared anonymous walk-in record.
      </div>
    );
  }

  if (walletQuery.isLoading) {
    return <div className="text-sm text-muted-foreground p-4">Loading wallet…</div>;
  }

  const wallet = walletQuery.data;
  if (!wallet) return null;

  const canTopup = hasPermission("wallets.create_topup");
  const canCashOut = hasPermission("wallets.create_cashout");

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Cash balance</p>
          <p className="text-3xl font-bold text-emerald-700">
            {formatKes(wallet.spendableBalance)}
          </p>
        </div>
        <div className="flex gap-2">
          {canTopup && (
            <Button onClick={() => setTopupOpen(true)} variant="default">
              Top up
            </Button>
          )}
          {canCashOut && wallet.spendableBalance > 0 && (
            <Button onClick={() => setCashOutOpen(true)} variant="outline">
              Cash out
            </Button>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Activity</p>
        {statementQuery.isLoading ? (
          <div className="text-sm text-muted-foreground">Loading activity…</div>
        ) : !statementQuery.data || statementQuery.data.length === 0 ? (
          <div className="rounded-md border p-4 text-sm text-muted-foreground">
            No approved activity yet.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Cause</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {statementQuery.data.map((e) => (
                <TableRow
                  key={e.id}
                  className={focusEntryId === e.id ? "bg-emerald-50/80" : undefined}
                >
                  <TableCell>{formatDate(e.createdAt)}</TableCell>
                  <TableCell>{e.type}</TableCell>
                  <TableCell className="max-w-xs truncate" title={e.cause ?? undefined}>
                    {e.cause}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{e.id.slice(0, 8).toUpperCase()}</TableCell>
                  <TableCell>
                    <Badge variant={e.status === "Reversed" ? "destructive" : "secondary"}>
                      {e.status}
                    </Badge>
                  </TableCell>
                  <TableCell className={`text-right ${directionStyles(e)}`}>
                    {e.direction === "Credit" ? "+" : "−"} {formatKes(e.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Loyalty points — rendered separately; cash and points are never mixed. */}
      <PointsBalanceCard customerId={customerId} />

      <TopupDialog customerId={customerId} open={topupOpen} onOpenChange={setTopupOpen} />
      <CashOutDialog
        customerId={customerId}
        spendableBalance={wallet.spendableBalance}
        open={cashOutOpen}
        onOpenChange={setCashOutOpen}
      />
    </div>
  );
};
